import React, { useState, useRef, useCallback } from "react";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Application } from "@/contexts/ApplicationContext";
import { Loader2, CheckCircle2, AlertCircle, FileText } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { uploadCVDocument } from "@/services/storageService";
import { createDocumentRecord } from "@/services/documentService";
import { cn } from "@/lib/utils";

interface CVUploadSectionProps {
  application: Application;
  setApplication: (app: Application) => void;
}

const CVUploadSection: React.FC<CVUploadSectionProps> = ({
  application,
  setApplication,
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<
    "idle" | "uploading" | "success" | "error"
  >("idle");
  const [dragActive, setDragActive] = useState(false);
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dropZoneRef = useRef<HTMLDivElement>(null);

  const processFile = useCallback(
    async (file: File) => {
      // Check file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast({
          title: "File too large",
          description: "Please select a file smaller than 10MB",
          variant: "destructive",
        });
        // Reset the file input
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        setUploadStatus("error");
        return;
      }

      // Check file type (PDF, DOC, DOCX)
      const validTypes = [
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ];
      if (!validTypes.includes(file.type)) {
        toast({
          title: "Invalid file type",
          description: "Please upload a PDF or Word document",
          variant: "destructive",
        });
        // Reset the file input
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        setUploadStatus("error");
        return;
      }

      // Get first name and last name from application context
      const firstName = application.basicDetails.firstName || "";
      const lastName = application.basicDetails.lastName || "";

      if (!firstName || !lastName) {
        toast({
          title: "Missing information",
          description:
            "Please fill in your first and last name before uploading your CV",
          variant: "destructive",
        });
        // Reset the file input
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        setUploadStatus("error");
        return;
      }

      // Automatically upload the file
      setIsUploading(true);
      setUploadStatus("uploading");

      try {
        // Use the dedicated CV upload function with first name and last name
        const uploadResult = await uploadCVDocument(file, firstName, lastName);

        if (uploadResult.success && uploadResult.url) {
          // Create document record in the database if application ID exists
          if (application.applicationId) {
            await createDocumentRecord(
              application.applicationId,
              "cv",
              uploadResult.url
            );
          }

          // Update application context with CV URL
          setApplication({
            ...application,
            basicDetails: {
              ...application.basicDetails,
              cv: uploadResult.url,
            },
          });

          toast({
            title: "CV uploaded successfully",
            description: "CV has been uploaded",
            variant: "default",
          });
          setUploadStatus("success");
        } else {
          toast({
            title: "Upload failed",
            description: uploadResult.message || "Failed to upload CV",
            variant: "destructive",
          });
          setUploadStatus("error");
        }
      } catch (error) {
        console.error("Error uploading CV:", error);
        toast({
          title: "Upload error",
          description: "An error occurred while uploading your CV",
          variant: "destructive",
        });
        setUploadStatus("error");
      } finally {
        setIsUploading(false);
      }
    },
    [application, setApplication, toast]
  );

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      await processFile(file);
    }
  };

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);

      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        const file = e.dataTransfer.files[0];
        await processFile(file);
      }
    },
    [processFile]
  );

  const handleButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <div className="space-y-4 mb-6">
      <div className="flex items-center px-6 pt-6">
        <h2 className="text-lg text-jungle font-bold">CV Upload</h2>
      </div>
      <Separator className="mt-2" />

      <div className="p-6 bg-transparent">
        <div className="space-y-2">
          <Label htmlFor="cv-upload" className="text-sm md:text-base">
            Upload CV
          </Label>
          <div className="flex flex-col space-y-4">
            <div
              ref={dropZoneRef}
              className={cn(
                "relative flex flex-col items-center justify-center w-full h-40 border-2 border-dashed rounded-lg cursor-pointer transition-colors",
                dragActive
                  ? "border-amber-500 bg-amber-50"
                  : "border-gray-300 hover:bg-gray-50",
                uploadStatus === "success"
                  ? "border-green-500 bg-green-50"
                  : "",
                uploadStatus === "error" ? "border-red-500 bg-red-50" : "",
                isUploading ? "pointer-events-none opacity-70" : ""
              )}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              onClick={handleButtonClick}
            >
              <input
                id="cv-upload"
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                className="hidden"
                accept=".pdf,.doc,.docx"
                disabled={isUploading}
              />

              {/* Status indicators */}
              {uploadStatus === "idle" && !application.basicDetails.cv && (
                <>
                  <div className="flex flex-col items-center justify-center pt-5 pb-6">
                    <FileText className="w-10 h-10 mb-3 text-gray-400" />
                    <p className="mb-2 text-sm text-gray-500">
                      <span className="font-semibold">Click to upload</span> or
                      drag and drop
                    </p>
                    <p className="text-xs text-gray-500">
                      PDF or Word document (max. 10MB)
                    </p>
                  </div>
                </>
              )}

              {isUploading && (
                <div className="flex flex-col items-center justify-center">
                  <Loader2 className="w-10 h-10 mb-3 text-amber-500 animate-spin" />
                  <p className="text-sm text-amber-600">Uploading your CV...</p>
                </div>
              )}

              {uploadStatus === "success" && (
                <div className="flex flex-col items-center justify-center">
                  <CheckCircle2 className="w-10 h-10 mb-3 text-green-500" />
                  <p className="text-sm text-green-600">
                    CV uploaded successfully
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Click to upload a different file
                  </p>
                </div>
              )}

              {uploadStatus === "error" && (
                <div className="flex flex-col items-center justify-center">
                  <AlertCircle className="w-10 h-10 mb-3 text-red-500" />
                  <p className="text-sm text-red-600">Upload failed</p>
                  <p className="text-xs text-gray-500 mt-1">
                    Click to try again
                  </p>
                </div>
              )}

              {/* Show file name if CV is uploaded but status is idle (e.g. on page load) */}
              {uploadStatus === "idle" && application.basicDetails.cv && (
                <div className="flex flex-col items-center justify-center">
                  <CheckCircle2 className="w-10 h-10 mb-3 text-green-500" />
                  <p className="text-sm text-green-600">
                    CV uploaded successfully
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Click to upload a different file
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CVUploadSection;
